import React, { useState, useEffect, useContext, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../../context/AuthContext';
import { workOrderApi } from '../../api/workOrderApi';
import WorkOrderFormModal from './WorkOrderFormModal';
import WorkOrderDetailModal from './WorkOrderDetailModal';
import './WorkOrderMatrix.css';
import './WorkOrderListPage.css';

function WorkOrderMatrix() {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);

  // ── 권한 판별 ────────────────────────────────────────────────
  const isSuperAdmin = (user?.userid === 'admin' || user?.id === 'admin') && (user?.role === 'admin' || user?.is_admin === 1 || user?.is_admin === true);
  const isRegularAdmin = !isSuperAdmin && (user?.role === 'admin' || user?.is_admin === 1 || user?.is_admin === true);
  const isAdminRole = isRegularAdmin; // 일반 관리자만 체크박스 노출
  const [adminAllChecked, setAdminAllChecked] = useState(false);
  const effectiveAdminAll = isSuperAdmin ? true : (isRegularAdmin ? adminAllChecked : false);

  const [matrix, setMatrix] = useState(null);
  const [urgent, setUrgent] = useState([]);
  const [urgentPage, setUrgentPage] = useState(1);
  const [isLoading, setIsLoading] = useState(true);

  const urgentPageSize = 5;
  const totalUrgentPages = Math.ceil((urgent?.length || 0) / urgentPageSize) || 1;
  const pageBlockSize = 10;
  const startPage = Math.floor((urgentPage - 1) / pageBlockSize) * pageBlockSize + 1;
  const endPage = Math.min(startPage + pageBlockSize - 1, totalUrgentPages);
  const urgentPageNumbers = Array.from({ length: Math.max(1, endPage - startPage + 1) }, (_, i) => startPage + i);

  // 담당자 및 상태 호버 팝오버 상태 및 타이머
  const [hoveredAttendees, setHoveredAttendees] = useState(null);
  const [hoveredStatus, setHoveredStatus] = useState(null);
  const hoverStatusTimerRef = useRef(null);
  const hoverAttendeesTimerRef = useRef(null);

  // ── 연도 필터 상태 (기본: 전체 연도, [현재+1, 현재, 현재-1, 현재-2] 4개년 기본 관리) ──
  const [selectedYear, setSelectedYear] = useState(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const y = urlParams.get('year');
    if (!y || y === 'all') return 'all';
    return parseInt(y, 10);
  });
  const [yearList, setYearList] = useState(() => {
    const cy = new Date().getFullYear();
    return [cy + 1, cy, cy - 1, cy - 2];
  });
  const [isYearOpen, setIsYearOpen] = useState(false);
  const yearDropdownRef = useRef(null);

  // 과거 3년 추가 핸들러
  const handleAddPastYears = (e) => {
    e.stopPropagation();
    setYearList(prev => {
      const minYear = Math.min(...prev);
      const newYears = [minYear - 1, minYear - 2, minYear - 3];
      return [...prev, ...newYears];
    });
  };

  // 과거 3년 축소 핸들러 (기본 4개년은 유지)
  const handleRemovePastYears = (e) => {
    e.stopPropagation();
    setYearList(prev => {
      if (prev.length <= 4) return prev;
      return prev.slice(0, prev.length - 3);
    });
  };

  // 기본 4개년 및 전체 연도로 초기화
  const handleResetYears = (e) => {
    e.stopPropagation();
    const cy = new Date().getFullYear();
    setYearList([cy + 1, cy, cy - 1, cy - 2]);
    setSelectedYear('all');
    setIsYearOpen(false);
    setUrgentPage(1);
  };

  // 팝오버 외부 클릭 시 닫기
  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (yearDropdownRef.current && !yearDropdownRef.current.contains(e.target)) {
        setIsYearOpen(false);
      }
    };
    document.addEventListener('click', handleOutsideClick);
    return () => document.removeEventListener('click', handleOutsideClick);
  }, []);

  // 모달 상태
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('create');
  const [modalWorkOrderId, setModalWorkOrderId] = useState(null);
  const [modalInitialTab, setModalInitialTab] = useState('order');

  const openCreateModal = () => { setModalMode('create'); setModalWorkOrderId(null); setModalInitialTab('order'); setModalOpen(true); };
  const openViewModal = (id, edit = false, initialTab = 'order') => { setModalMode('view'); setModalWorkOrderId(id); setModalInitialTab(initialTab); setModalOpen(true); };
  const closeModal = () => { setModalOpen(false); setModalMode('create'); setModalWorkOrderId(null); setModalInitialTab('order'); };
  const handleSaved = () => { closeModal(); fetchMatrix(); };
  const handleDeleted = () => { closeModal(); fetchMatrix(); };

  // ── 빠른 액션 핸들러 ──
  const handleAccept = async (id) => {
    if (!window.confirm('업무를 접수(시작)하시겠습니까?')) return;
    try {
      await workOrderApi.changeAction(id, { action: 'accept' });
      fetchMatrix();
    } catch (err) {
      alert('접수 처리 실패: ' + (err.response?.data?.message || err.message));
    }
  };

  const handleRemind = async (id) => {
    if (!window.confirm('담당자에게 독촉 알림을 발송하시겠습니까?')) return;
    try {
      await workOrderApi.sendReminder(id);
      alert('독촉 알림이 발송되었습니다.');
    } catch (err) {
      alert('알림 발송 실패: ' + (err.response?.data?.message || err.message));
    }
  };

  // Matrix 통계 데이터 조회 (선택된 연도 전달, 'all'일 때는 전체 조회)
  const fetchMatrix = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await workOrderApi.getMatrixCounts({
        ...(effectiveAdminAll ? { adminAll: true } : {}),
        ...(selectedYear && selectedYear !== 'all' ? { year: selectedYear } : {})
      });
      const payload = res?.data || res || {};
      setMatrix(payload);
      setUrgent(payload.urgent || res?.urgent || []);
    } catch (err) {
      console.error('[WorkOrderMatrix] 통계 조회 실패:', err);
    } finally {
      setIsLoading(false);
    }
  }, [effectiveAdminAll, selectedYear]);

  useEffect(() => {
    fetchMatrix();
  }, [fetchMatrix]);

  // 페이지 이동 헬퍼 (status와 함께 현재 선택된 year 전달, 'all'일 때는 미포함하여 전체로 이동)
  const handleNavigate = (path, statusFilter) => {
    const params = new URLSearchParams();
    if (statusFilter) params.set('status', statusFilter);
    if (selectedYear && selectedYear !== 'all') params.set('year', String(selectedYear));
    if (isAdminRole) params.set('adminAll', adminAllChecked ? 'true' : 'false');
    const queryString = params.toString();
    navigate(queryString ? `${path}?${queryString}` : path);
  };

  // ── 다크 헤더용 프리미엄 연도 드롭다운 렌더러 ──
  const renderYearDropdown = () => (
    <div className="position-relative" ref={yearDropdownRef} style={{ display: 'inline-block' }}>
      <button
        type="button"
        onClick={() => setIsYearOpen(prev => !prev)}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '6px',
          height: '28px',
          padding: '0 11px',
          border: '1px solid rgba(245, 158, 11, 0.65)',
          borderRadius: '8px',
          backgroundColor: 'rgba(245, 158, 11, 0.16)',
          color: '#ffffff',
          fontWeight: 700,
          fontSize: '0.8rem',
          cursor: 'pointer',
          boxShadow: '0 2px 6px rgba(0, 0, 0, 0.25)',
          transition: 'all 0.18s ease'
        }}
        title="조회 연도 선택"
        onMouseEnter={e => { e.currentTarget.style.backgroundColor = 'rgba(245, 158, 11, 0.28)'; }}
        onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'rgba(245, 158, 11, 0.16)'; }}
      >
        <i className="far fa-calendar-alt" style={{ fontSize: '0.86rem', color: '#fbbf24' }} />
        <span style={{ color: '#ffffff' }}>{selectedYear === 'all' ? '전체 연도' : `${selectedYear}년`}</span>
        <i className="fas fa-chevron-down" style={{ fontSize: '0.7rem', color: '#fcd34d', marginLeft: '3px' }} />
      </button>

      {isYearOpen && (
        <div
          style={{
            position: 'absolute',
            top: 'calc(100% + 5px)',
            left: 0,
            zIndex: 1050,
            minWidth: '185px',
            backgroundColor: '#ffffff',
            border: '1px solid #cbd5e1',
            borderRadius: '10px',
            boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.2), 0 8px 10px -6px rgba(0, 0, 0, 0.15)',
            padding: '6px',
            display: 'flex',
            flexDirection: 'column',
            gap: '3px'
          }}
        >
          {/* 팝오버 상단 미니 헤더 */}
          <div
            style={{
              padding: '3px 8px 5px 8px',
              borderBottom: '1px solid #f1f5f9',
              marginBottom: '2px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}
          >
            <span style={{ fontSize: '0.72rem', fontWeight: 700, color: '#64748b', letterSpacing: '-0.2px' }}>
              <i className="far fa-calendar-check me-1" style={{ color: '#2563eb' }} />
              조회 연도
            </span>
          </div>

          {/* 1. 최상단: 전체 연도 옵션 */}
          <button
            type="button"
            onClick={() => {
              setSelectedYear('all');
              setIsYearOpen(false);
              setUrgentPage(1);
            }}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '7px 10px',
              border: 'none',
              borderRadius: '6px',
              backgroundColor: selectedYear === 'all' ? '#eff6ff' : 'transparent',
              color: selectedYear === 'all' ? '#1d4ed8' : '#1e293b',
              fontWeight: selectedYear === 'all' ? 700 : 600,
              fontSize: '0.86rem',
              cursor: 'pointer',
              textAlign: 'left',
              transition: 'all 0.15s ease'
            }}
            onMouseEnter={e => {
              if (selectedYear !== 'all') e.currentTarget.style.backgroundColor = '#f1f5f9';
            }}
            onMouseLeave={e => {
              if (selectedYear !== 'all') e.currentTarget.style.backgroundColor = 'transparent';
            }}
          >
            <span className="d-flex align-items-center gap-1.5">
              <i className="fas fa-layer-group" style={{ fontSize: '0.8rem', color: selectedYear === 'all' ? '#2563eb' : '#64748b' }} />
              전체 연도
            </span>
            {selectedYear === 'all' && <i className="fas fa-check" style={{ fontSize: '0.75rem', color: '#2563eb' }} />}
          </button>

          {/* 전체 연도 구분선 (밑줄) */}
          <div style={{ borderBottom: '1px solid #e2e8f0', margin: '3px 0 2px 0' }} />

          {/* 2. 연도 목록 (최신순) */}
          <div style={{ maxHeight: '190px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '2px', padding: '1px 0' }}>
            {yearList.map(y => {
              const isSelected = selectedYear === y;
              return (
                <button
                  key={y}
                  type="button"
                  onClick={() => {
                    setSelectedYear(y);
                    setIsYearOpen(false);
                    setUrgentPage(1);
                  }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '6px 10px',
                    border: 'none',
                    borderRadius: '6px',
                    backgroundColor: isSelected ? '#eff6ff' : 'transparent',
                    color: isSelected ? '#1d4ed8' : '#334155',
                    fontWeight: isSelected ? 700 : 500,
                    fontSize: '0.86rem',
                    cursor: 'pointer',
                    textAlign: 'left',
                    transition: 'all 0.15s ease'
                  }}
                  onMouseEnter={e => {
                    if (!isSelected) e.currentTarget.style.backgroundColor = '#f1f5f9';
                  }}
                  onMouseLeave={e => {
                    if (!isSelected) e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                >
                  <span>{y}년</span>
                  {isSelected && <i className="fas fa-check" style={{ fontSize: '0.75rem', color: '#2563eb' }} />}
                </button>
              );
            })}
          </div>

          {/* 하단 3단 컨트롤 툴바 */}
          <div
            style={{
              borderTop: '1px solid #e2e8f0',
              paddingTop: '6px',
              marginTop: '4px',
              display: 'flex',
              alignItems: 'center',
              gap: '4px'
            }}
          >
            <button
              type="button"
              onClick={handleAddPastYears}
              title="과거 3개년 불러오기"
              style={{
                flex: 1,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: '28px',
                border: 'none',
                borderRadius: '6px',
                backgroundColor: '#eff6ff',
                color: '#2563eb',
                fontSize: '0.72rem',
                fontWeight: 700,
                cursor: 'pointer',
                transition: 'all 0.15s ease',
                padding: '0 2px',
                whiteSpace: 'nowrap'
              }}
              onMouseEnter={e => { e.currentTarget.style.backgroundColor = '#dbeafe'; }}
              onMouseLeave={e => { e.currentTarget.style.backgroundColor = '#eff6ff'; }}
            >
              + 3년 추가
            </button>
            <button
              type="button"
              onClick={handleResetYears}
              title="현재 기준 4개년으로 초기화"
              style={{
                width: '32px',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: '28px',
                border: 'none',
                borderRadius: '6px',
                backgroundColor: '#f8fafc',
                color: '#475569',
                fontSize: '0.74rem',
                cursor: 'pointer',
                transition: 'all 0.15s ease'
              }}
              onMouseEnter={e => { e.currentTarget.style.backgroundColor = '#f1f5f9'; }}
              onMouseLeave={e => { e.currentTarget.style.backgroundColor = '#f8fafc'; }}
            >
              <i className="fas fa-undo-alt" />
            </button>
            <button
              type="button"
              onClick={handleRemovePastYears}
              title="추가된 과거 연도 3개년 접기"
              disabled={yearList.length <= 4}
              style={{
                flex: 1,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: '28px',
                border: 'none',
                borderRadius: '6px',
                backgroundColor: yearList.length <= 4 ? '#f1f5f9' : '#fef2f2',
                color: yearList.length <= 4 ? '#94a3b8' : '#dc2626',
                fontSize: '0.72rem',
                fontWeight: 700,
                cursor: yearList.length <= 4 ? 'not-allowed' : 'pointer',
                transition: 'all 0.15s ease',
                padding: '0 2px',
                whiteSpace: 'nowrap'
              }}
              onMouseEnter={e => { if (yearList.length > 4) e.currentTarget.style.backgroundColor = '#fee2e2'; }}
              onMouseLeave={e => { if (yearList.length > 4) e.currentTarget.style.backgroundColor = '#fef2f2'; }}
            >
              - 3년 축소
            </button>
          </div>
        </div>
      )}
    </div>
  );

  // 사용자 표시명
  const authorName = user
    ? `${user.name || ''}${user.position ? ' ' + user.position : ''}${user.dept_name ? ` (${user.dept_name})` : ''}`
    : '사용자';

  // ── 통계 데이터 안전 집계 ────────────────────────────────────
  const rawReceived = matrix?.received || {};
  const rawSent = matrix?.sent || {};
  const rawReferred = matrix?.referred || matrix?.cc || {};
  const rawCompleted = matrix?.completed || {};

  // 1. 지시받은 업무
  const recStop = Number(rawReceived.stop || 0);
  const recRequest = Number(rawReceived.request ?? rawReceived.pending ?? 0);
  const recProgress = Number(rawReceived.progress || 0);
  const recReview = Number(rawReceived.review || 0);
  const recDelay = Number(rawReceived.delay ?? rawReceived.delayed ?? 0);
  const recDelayRequest = Number(rawReceived.delay_request || 0);
  const recDelayProgress = Number(rawReceived.delay_progress || 0);
  const recDone = Number(rawReceived.done ?? rawReceived.completed ?? rawCompleted.received_done ?? 0);
  const recTotal = Number(rawReceived.total ?? (recStop + recRequest + recProgress + recReview + recDelay));

  // 2. 지시내린 업무
  const sentStop = Number(rawSent.stop || 0);
  const sentRequest = Number(rawSent.request ?? rawSent.pending ?? 0);
  const sentProgress = Number(rawSent.progress || 0);
  const sentReview = Number(rawSent.review || 0);
  const sentDelay = Number(rawSent.delay ?? rawSent.delayed ?? 0);
  const sentDelayRequest = Number(rawSent.delay_request || 0);
  const sentDelayProgress = Number(rawSent.delay_progress || 0);
  const sentDone = Number(rawSent.done ?? rawSent.completed ?? rawCompleted.sent_done ?? 0);
  const sentTotal = Number(rawSent.total ?? (sentStop + sentRequest + sentProgress + sentReview + sentDelay));

  // 3. 참조 업무
  const refStop = Number(rawReferred.stop || 0);
  const refProgress = Number(rawReferred.progress ?? rawReferred.unread ?? 0);
  const refDelay = Number(rawReferred.delay ?? rawReferred.delayed ?? 0);
  const refDone = Number(rawReferred.done ?? rawReferred.completed ?? rawCompleted.referred_done ?? 0);
  const refTotal = Number(rawReferred.total ?? (refStop + refProgress + refDelay));

  // 4. 완료된 업무 합계
  const compTotal = Number(rawCompleted.total ?? (recDone + sentDone + refDone));

  // D-Day 계산 헬퍼
  const getDDay = (dueDateStr) => {
    if (!dueDateStr) return null;
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const due = new Date(dueDateStr); due.setHours(0, 0, 0, 0);
    const diff = Math.round((due - today) / (1000 * 60 * 60 * 24));
    if (diff > 0) return { text: `D-${diff}`, cls: diff <= 3 ? 'text-warning fw-bold' : 'text-secondary' };
    if (diff === 0) return { text: 'D-Day', cls: 'text-danger fw-bold' };
    return { text: `+${Math.abs(diff)}일 지연`, cls: 'text-danger fw-bold' };
  };

  // 긴급/지연 위젯 아이템 상태 판정 (리스트 표준 100% 동일 파이프라인: 접수지연 / 진행지연 / 오늘마감 / 기한임박 / 중단 등)
  const getUrgentItemStatus = (item) => {
    if (!item) return { label: '-', bg: '#f1f5f9', color: '#64748b', border: '#e2e8f0', delayInfo: null };

    const assignees = item.assignees || [];
    const isJoint = item.completion_type === 'joint' || item.completion_type === 'collective';

    const formatName = (a) => {
      const u = a.user || {};
      const pos = u.org?.position || u.position || a.position || '';
      const name = u.name || a.name || u.userid || '-';
      return `${name}${pos ? ' ' + pos : ''}`.trim();
    };

    // 1. 완료자
    const dones = assignees.filter(a => {
      return ['done', 'completed'].includes(a.status) || (item.reports || []).some(r => String(r.user_id) === String(a.user_id) && r.approval_status === 'approved' && (r.is_final || Number(r.progress_rate) === 100));
    }).map(formatName);

    // 2. 진행중
    const progresses = assignees.filter(a => {
      const isDone = ['done', 'completed'].includes(a.status) || (item.reports || []).some(r => String(r.user_id) === String(a.user_id) && r.approval_status === 'approved' && (r.is_final || Number(r.progress_rate) === 100));
      if (isDone) return false;
      const hasReport = (item.reports || []).some(r => String(r.user_id) === String(a.user_id));
      return hasReport || a.status === 'progress' || a.status === 'review' || a.status === 'submitted';
    }).map(formatName);

    // 3. 미접수
    const pendings = assignees.filter(a => {
      const isDone = ['done', 'completed'].includes(a.status) || (item.reports || []).some(r => String(r.user_id) === String(a.user_id) && r.approval_status === 'approved' && (r.is_final || Number(r.progress_rate) === 100));
      if (isDone) return false;
      const hasReport = (item.reports || []).some(r => String(r.user_id) === String(a.user_id));
      return !hasReport && a.status === 'pending';
    }).map(formatName);

    // 복수 담당자 상세 현황
    let commonMemberInfo = null;
    if (assignees.length >= 2 && !item.deleted_at && !['temp', 'draft'].includes(item.status)) {
      const total = assignees.length;
      const doneCount = dones.length;
      const ongoingCount = progresses.length;
      const pendingCount = pendings.length;

      let summaryText = `총 ${total}명의 담당자 중 `;
      const parts = [];
      if (doneCount > 0) parts.push(`완료 ${doneCount}명`);
      if (ongoingCount > 0) parts.push(`진행중 ${ongoingCount}명`);
      if (pendingCount > 0) parts.push(`미접수 ${pendingCount}명`);
      summaryText += (parts.length > 0 ? parts.join(', ') : '진행중') + '입니다.';

      commonMemberInfo = {
        type: 'members',
        message: summaryText,
        dones,
        progresses,
        pendings
      };
    }

    if (item.deleted_at) {
      return { label: '삭제됨', bg: '#fef2f2', color: '#dc2626', border: '#fecaca', delayInfo: null };
    }
    if (item.status === 'temp' || item.status === 'draft') {
      return { label: '임시저장', bg: '#f1f5f9', color: '#475569', border: '#e2e8f0', delayInfo: null };
    }
    if (item.status === 'canceled' || item.status === 'cancel') {
      return {
        label: '취소됨',
        bg: '#fff7ed',
        color: '#ea580c',
        border: '#fed7aa',
        delayInfo: { type: 'canceled', title: '취소됨', message: '요청자에 의해 취소된 업무협조 건입니다.', memberInfo: commonMemberInfo, pendings, progresses, dones }
      };
    }
    if (item.status === 'stop') {
      return {
        label: '중단',
        bg: '#fff7ed',
        color: '#c2410c',
        border: '#fed7aa',
        delayInfo: { type: 'stop', title: '중단', message: '요청자에 의해 일시 중단된 협조 업무입니다.', memberInfo: commonMemberInfo, pendings, progresses, dones }
      };
    }

    const today = new Date(); today.setHours(0, 0, 0, 0);
    const due = item.due_date ? new Date(item.due_date) : null;
    if (due) due.setHours(0, 0, 0, 0);
    const diff = due ? Math.round((due - today) / (1000 * 60 * 60 * 24)) : 0;
    const isDelayed = !['done', 'completed', 'stop', 'cancel', 'canceled'].includes(item.status) && !item.deleted_at && due && diff < 0;

    if (item.status === 'done' || item.status === 'completed') {
      return {
        label: '완료',
        bg: '#f0fdf4',
        color: '#15803d',
        border: '#bbf7d0',
        delayInfo: { type: 'done', title: '완료', message: '최종 완료 종결된 업무입니다.', memberInfo: commonMemberInfo, pendings, progresses, dones }
      };
    }

    if (!isDelayed) {
      if (diff === 0) {
        let displayLabel = '오늘마감';
        if (assignees.length >= 2) {
          const total = assignees.length;
          const doneCount = dones.length;
          if (doneCount > 0 && doneCount < total) {
            displayLabel = `오늘마감 (${doneCount}/${total})`;
          }
        }
        return {
          label: displayLabel,
          bg: '#fef3c7',
          color: '#b45309',
          border: '#fde68a',
          delayInfo: { type: 'today', title: displayLabel, message: '오늘 마감 예정인 업무입니다.', memberInfo: commonMemberInfo, pendings, progresses, dones }
        };
      }
      if (diff > 0 && diff <= 2) {
        let displayLabel = '기한임박';
        if (assignees.length >= 2) {
          const total = assignees.length;
          const doneCount = dones.length;
          if (doneCount > 0 && doneCount < total) {
            displayLabel = `기한임박 (${doneCount}/${total})`;
          }
        }
        return {
          label: displayLabel,
          bg: '#fff7ed',
          color: '#c2410c',
          border: '#ffedd5',
          delayInfo: { type: 'imminent', title: displayLabel, message: `처리 기한이 D-${diff}일 남은 업무협조입니다.`, memberInfo: commonMemberInfo, pendings, progresses, dones }
        };
      }

      let displayLabel = '진행중';
      if (assignees.length >= 2) {
        const total = assignees.length;
        const doneCount = dones.length;
        const ongoingCount = progresses.length;

        if (doneCount > 0 && doneCount < total) {
          displayLabel = `진행중 (${doneCount}/${total})`;
        } else if (doneCount === 0 && ongoingCount > 0 && pendings.length > 0) {
          displayLabel = `진행중 (${ongoingCount}/${total})`;
        }
      }

      return {
        label: displayLabel,
        bg: '#eff6ff',
        color: '#1d4ed8',
        border: '#bfdbfe',
        delayInfo: { type: 'normal', title: displayLabel, message: '정상 일정으로 진행 중인 업무입니다.', memberInfo: commonMemberInfo, pendings, progresses, dones }
      };
    }

    // 지연 상태일 때
    let label = '진행지연';
    let delayType = 'progress';

    if (assignees.length === 0) {
      label = item.status === 'request' ? '접수지연' : '진행지연';
      delayType = item.status === 'request' ? 'request' : 'progress';
    } else if (isJoint) {
      const hasAccepted = assignees.some(a => a.status !== 'pending');
      if (!hasAccepted) {
        label = '접수지연';
        delayType = 'request';
      } else {
        label = '진행지연';
        delayType = 'progress';
      }
    } else {
      if (pendings.length > 0 && progresses.length > 0) {
        label = '접수·진행지연';
        delayType = 'mixed';
      } else if (pendings.length > 0 && progresses.length === 0) {
        label = '접수지연';
        delayType = 'request';
      } else {
        label = '진행지연';
        delayType = 'progress';
      }
    }

    const delayDaysText = `+${Math.abs(diff)}일 지연`;

    let delayDesc = '';
    if (delayType === 'request') {
      delayDesc = '마감일이 지났으나 담당자가 아직 업무를 접수(시작)하지 않은 상태입니다.';
    } else if (delayType === 'progress') {
      delayDesc = '마감일이 지났으나 처리결과가 완료되지 않고 여전히 진행 중인 상태입니다.';
    } else if (delayType === 'mixed') {
      delayDesc = '마감일이 지난 상태에서 미접수자와 진행 중인 담당자가 함께 존재하는 상태입니다.';
    }

    return {
      label,
      bg: '#fef2f2',
      color: '#b91c1c',
      border: '#fecaca',
      delayInfo: {
        type: 'delay',
        delayType,
        title: `${label} (${delayDaysText})`,
        message: delayDesc,
        pendings,
        progresses,
        dones,
        memberInfo: commonMemberInfo
      }
    };
  };

  // 긴급/지연 위젯 지시자 렌더링
  const renderUrgentAuthor = (author) => {
    if (!author) return <span className="text-muted small">-</span>;
    const pos = author.org?.position || author.position || '';
    const name = `${author.name || author.userid || '-'}${pos ? ' ' + pos : ''}`.trim();
    return (
      <div
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '4px',
          background: '#f4f5f7',
          padding: '3px 6px',
          borderRadius: '4px',
          fontSize: '0.78rem',
          color: '#334155',
          fontWeight: 600,
          maxWidth: '100%',
          overflow: 'hidden'
        }}
        title={name}
      >
        <i className="fas fa-user" style={{ color: '#6b7280', flexShrink: 0, fontSize: '0.78rem' }} />
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{name}</span>
      </div>
    );
  };

  // 긴급/지연 위젯 담당자 렌더링 (목록 페이지 표준 100% 동일 뱃지 & 호버 팝오버)
  const renderUrgentAssignees = (assignees) => {
    if (!assignees || assignees.length === 0) return <span className="text-muted small">-</span>;

    const fullList = assignees.map(a => {
      const u = a.user || {};
      const pos = u.org?.position || u.position || a.position || '';
      const dept = u.org?.department || u.department || a.department || '';
      return `${u.name || a.name || u.userid || '-'}${pos ? ' ' + pos : ''}${dept ? ' (' + dept + ')' : ''}`;
    });

    const firstItem = assignees[0];
    const firstUser = firstItem.user || {};
    const firstPos = firstUser.org?.position || firstUser.position || firstItem.position || '';
    const firstName = `${firstUser.name || firstItem.name || firstUser.userid || '-'}${firstPos ? ' ' + firstPos : ''}`;
    const displayText = assignees.length > 1 ? `${firstName} 외 ${assignees.length - 1}명` : firstName;

    const badgeStyle = {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '6px',
      background: '#f4f5f7',
      padding: '4px 8px',
      borderRadius: '4px',
      fontSize: '0.8rem',
      color: '#334155',
      fontWeight: 600,
      maxWidth: '100%',
      overflow: 'hidden'
    };

    if (assignees.length <= 1) {
      return (
        <div style={badgeStyle} title={fullList.join(', ')}>
          <i className="fas fa-user" style={{ color: '#6b7280', flexShrink: 0, fontSize: '0.82rem' }} />
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{displayText}</span>
        </div>
      );
    }

    return (
      <div
        style={{ display: 'inline-block', maxWidth: '100%', cursor: 'pointer' }}
        onMouseEnter={(e) => {
          e.stopPropagation();
          if (hoverAttendeesTimerRef.current) clearTimeout(hoverAttendeesTimerRef.current);
          const rect = e.currentTarget.getBoundingClientRect();
          const popoverHeight = 180;
          const spaceAbove = rect.top;
          const spaceBelow = window.innerHeight - rect.bottom;
          const showDown = spaceAbove < popoverHeight && spaceBelow >= spaceAbove;

          setHoveredAttendees({
            list: fullList,
            x: rect.left + rect.width / 2,
            y: showDown ? rect.bottom + 8 : rect.top - 8,
            showDown
          });
        }}
        onMouseLeave={() => {
          hoverAttendeesTimerRef.current = setTimeout(() => {
            setHoveredAttendees(null);
          }, 180);
        }}
      >
        <div style={badgeStyle}>
          <i className="fas fa-user" style={{ color: '#6b7280', flexShrink: 0, fontSize: '0.82rem' }} />
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{displayText}</span>
        </div>
      </div>
    );
  };

  // ── 긴급/지연 위젯 빠른 액션 버튼 렌더러 (리스트 표준 100% 동일) ──
  const renderUrgentActions = (item) => {
    const uid = String(user?.id || '');
    const uuid = String(user?.userid || '');
    const uname = String(user?.name || '');

    const myAssignee = (item.assignees || []).find(a => {
      const u = a.user || {};
      return (uid && (String(a.user_id) === uid || String(u.id) === uid)) ||
        (uuid && (String(a.user_id) === uuid || String(u.userid) === uuid)) ||
        (uname && (a.name === uname || u.name === uname));
    });

    const isAssignee = Boolean(myAssignee);
    const isAuthor = (uid && String(item.author_id) === uid) || (uuid && String(item.author?.userid) === uuid) || (uname && item.author?.name === uname);
    const today = new Date().toISOString().split('T')[0];
    const dueStr = item.due_date ? String(item.due_date).split('T')[0] : null;
    const isDelayed = item.status !== 'done' && item.status !== 'cancel' && dueStr && dueStr < today;

    const btnStyle = (bg, color, border) => ({
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '3px 8px',
      fontSize: '0.74rem',
      fontWeight: 600,
      borderRadius: '4px',
      backgroundColor: bg,
      color: color,
      border: `1px solid ${border}`,
      whiteSpace: 'nowrap',
      cursor: 'pointer',
      transition: 'all 0.15s ease'
    });

    // 완료 상태
    if (item.status === 'done') {
      return (
        <span style={{ display: 'inline-block', padding: '3px 8px', fontSize: '0.72rem', fontWeight: 600, borderRadius: '4px', backgroundColor: '#f0fdf4', color: '#15803d', border: '1px solid #bbf7d0', whiteSpace: 'nowrap', cursor: 'default', userSelect: 'none' }}>
          완료
        </span>
      );
    }

    // 1. 담당자 전용 액션
    if (isAssignee) {
      if (myAssignee.status === 'pending') {
        return (
          <button
            type="button"
            className="wo-pulse-blue"
            style={btnStyle('#eff6ff', '#1d4ed8', '#bfdbfe')}
            onClick={(e) => { e.stopPropagation(); handleAccept(item.id); }}
            onMouseEnter={e => { e.currentTarget.style.backgroundColor = '#dbeafe'; }}
            onMouseLeave={e => { e.currentTarget.style.backgroundColor = '#eff6ff'; }}
          >
            접수
          </button>
        );
      }
      if (myAssignee.status === 'progress') {
        return (
          <button
            type="button"
            className="wo-pulse-green"
            style={btnStyle('#ecfdf5', '#047857', '#a7f3d0')}
            onClick={(e) => { e.stopPropagation(); openViewModal(item.id, false, 'reports'); }}
            onMouseEnter={e => { e.currentTarget.style.backgroundColor = '#d1fae5'; }}
            onMouseLeave={e => { e.currentTarget.style.backgroundColor = '#ecfdf5'; }}
          >
            처리결과 작성
          </button>
        );
      }
    }

    // 2. 요청자 전용 액션
    if (isAuthor) {
      if (item.status === 'review') {
        return (
          <button
            type="button"
            className="wo-pulse-purple"
            style={btnStyle('#eef2ff', '#4338ca', '#c7d2fe')}
            onClick={(e) => { e.stopPropagation(); openViewModal(item.id, false, 'reports'); }}
            onMouseEnter={e => { e.currentTarget.style.backgroundColor = '#e0e7ff'; }}
            onMouseLeave={e => { e.currentTarget.style.backgroundColor = '#eef2ff'; }}
          >
            처리결과 검토
          </button>
        );
      }
      if (isDelayed) {
        return (
          <button
            type="button"
            className="wo-pulse-red"
            style={btnStyle('#fef2f2', '#b91c1c', '#fecaca')}
            onClick={(e) => { e.stopPropagation(); handleRemind(item.id); }}
            onMouseEnter={e => { e.currentTarget.style.backgroundColor = '#fee2e2'; }}
            onMouseLeave={e => { e.currentTarget.style.backgroundColor = '#fef2f2'; }}
          >
            독촉 발송
          </button>
        );
      }
    }

    // 3. 기본 액션 (열람)
    return (
      <button
        type="button"
        style={btnStyle('#f8fafc', '#475569', '#e2e8f0')}
        onClick={(e) => { e.stopPropagation(); openViewModal(item.id); }}
        onMouseEnter={e => { e.currentTarget.style.backgroundColor = '#f1f5f9'; }}
        onMouseLeave={e => { e.currentTarget.style.backgroundColor = '#f8fafc'; }}
      >
        열람
      </button>
    );
  };

  return (
    <div className="container-fluid px-0" style={{ marginTop: '-1.25rem' }}>
      {/* ── 1. 상단 독립 헤더 바 (프리미엄 딥 다크 네이비 카드) ── */}
      <div
        className="card mb-3 border-0"
        style={{
          background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)',
          borderRadius: '16px',
          boxShadow: '0 8px 24px -4px rgba(15, 23, 42, 0.22)',
          color: '#ffffff'
        }}
      >
        <div className="card-body d-flex justify-content-between align-items-center py-3.5 px-4">
          <div className="d-flex align-items-center">
            <div
              className="d-flex justify-content-center align-items-center me-3 flex-shrink-0"
              style={{
                width: '46px',
                height: '46px',
                borderRadius: '14px',
                background: 'rgba(59, 130, 246, 0.15)',
                border: '1px solid rgba(96, 165, 250, 0.3)',
                color: '#60a5fa'
              }}
            >
              <i className="fas fa-th-large fs-5" />
            </div>
            <div>
              <div className="d-flex align-items-center mb-1">
                <h4 className="mb-0 fw-bold text-white d-flex align-items-center" style={{ letterSpacing: '-0.02em' }}>
                  업무협조 종합 현황
                  <span
                    style={{
                      marginLeft: '5px',
                      display: 'inline-flex',
                      alignItems: 'center',
                      height: '24px',
                      padding: '0 10px',
                      backgroundColor: 'rgba(255, 255, 255, 0.08)',
                      border: '1px solid rgba(255, 255, 255, 0.18)',
                      borderRadius: '12px',
                      fontSize: '0.74rem',
                      color: '#93c5fd',
                      fontWeight: 600,
                      gap: '5px'
                    }}
                  >
                    <i className="fas fa-user-circle" style={{ color: '#60a5fa' }} />
                    <span>{authorName}</span>
                  </span>
                </h4>
                <div style={{ marginLeft: '18px' }}>
                  {renderYearDropdown()}
                </div>
              </div>
              <span className="fw-medium" style={{ fontSize: '0.84rem', color: '#94a3b8' }}>
                담당, 요청, 참조 협조 업무의 전체 진행 및 마감 현황을 한눈에 관리하세요.
              </span>
            </div>
          </div>

          <div className="d-flex align-items-center gap-2">
            {isAdminRole && (
              <label
                className="d-flex align-items-center gap-2 m-0"
                style={{
                  fontSize: '0.85rem',
                  fontWeight: 600,
                  color: '#e2e8f0',
                  cursor: 'pointer',
                  userSelect: 'none',
                  marginRight: '6px'
                }}
              >
                <input
                  type="checkbox"
                  className="form-check-input m-0"
                  checked={adminAllChecked}
                  onChange={(e) => {
                    setAdminAllChecked(e.target.checked);
                    setUrgentPage(1);
                  }}
                  style={{
                    width: '16px',
                    height: '16px',
                    cursor: 'pointer',
                    accentColor: '#3b82f6'
                  }}
                />
                <span>관리자 전체보기</span>
              </label>
            )}
            <button
              className="btn btn-sm fw-bold d-flex align-items-center gap-1.5"
              onClick={fetchMatrix}
              style={{
                height: '38px',
                padding: '0 14px',
                backgroundColor: 'rgba(255, 255, 255, 0.08)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                borderRadius: '10px',
                color: '#e2e8f0'
              }}
            >
              <i className="fas fa-sync-alt" style={{ fontSize: '0.8rem', color: '#93c5fd' }} /> 새로고침
            </button>
            <button
              className="btn btn-sm fw-bold d-flex align-items-center gap-1.5 text-white"
              onClick={openCreateModal}
              style={{
                height: '38px',
                padding: '0 16px',
                background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                border: 'none',
                borderRadius: '10px',
                boxShadow: '0 4px 14px rgba(37, 99, 235, 0.4)'
              }}
            >
              <i className="fas fa-plus" /> 업무협조 요청
            </button>
          </div>
        </div>
      </div>

      {isLoading ? (
        <div className="card border-0 shadow-sm rounded-3 py-5 text-center my-4">
          <div className="spinner-border text-primary mx-auto mb-3" role="status" />
          <span className="text-secondary">업무 현황 데이터를 불러오는 중입니다...</span>
        </div>
      ) : (
        /* ── 2. 본문 대시보드 곡선 캔버스 패널 ── */
        <div
          className="rounded-4 mb-0"
          style={{
            backgroundColor: '#f4f5f7',
            border: '1px solid #cbd5e1',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.04)',
            padding: '20px 24px',
            marginBottom: '0px'
          }}
        >
          {/* ── 4대 메뉴(지시받은/지시내린/참조/완료) 섹션형 위젯 그리드 ── */}
          <div
            className="row gx-4 gy-0 mb-0"
            style={{
              '--bs-gutter-y': '0px',
              rowGap: '0px',
              marginBottom: '0px'
            }}
          >

            {/* ── 위젯 1: 지시받은 업무 (내가 담당자) ── */}
            <div className="col-12 col-xl-6">
              <div
                className="card border-0 shadow-sm rounded-3 overflow-hidden"
                style={{ borderTop: '3px solid #3b82f6' }}
              >
                <div
                  className="card-header border-bottom d-flex justify-content-between align-items-center"
                  style={{
                    background: 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)',
                    borderBottom: '1px solid #bfdbfe',
                    padding: '12px 18px'
                  }}
                >
                  <div className="d-flex align-items-center gap-3">
                    <div
                      className="rounded-circle d-flex align-items-center justify-content-center shadow-sm text-white flex-shrink-0"
                      style={{ width: '36px', height: '36px', background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)' }}
                    >
                      <i className="fas fa-inbox fs-6" />
                    </div>
                    <div className="d-flex flex-column justify-content-center">
                      <div className="fw-bold" style={{ fontSize: '0.92rem', color: '#1e40af', lineHeight: 1.2, marginBottom: '2px' }}>요청받은 업무</div>
                      <div className="small" style={{ fontSize: '0.75rem', color: '#3b82f6', lineHeight: 1 }}>내가 담당자로 지정되어 처리할 업무</div>
                    </div>
                  </div>
                  <div className="d-flex align-items-center gap-2">
                    <div className="bg-white rounded-pill px-3 py-1 shadow-sm d-flex align-items-center gap-1 border border-primary border-opacity-25">
                      <span className="fw-bold text-primary" style={{ fontSize: '0.78rem' }}>{recTotal}</span>
                      <span className="text-secondary fw-medium" style={{ fontSize: '0.78rem' }}>건</span>
                    </div>
                    <button
                      className="btn btn-primary btn-sm py-1 px-3 ms-1 shadow-sm rounded-pill fw-medium"
                      style={{ fontSize: '0.75rem' }}
                      onClick={() => handleNavigate('/work-order/received')}
                    >
                      목록 <i className="fas fa-chevron-right ms-1" />
                    </button>
                  </div>
                </div>

                <div className="card-body" style={{ padding: '14px 16px' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(88px, 1fr))', gap: '8px' }}>
                    {/* 1. 중단 */}
                    <div
                      className="rounded-2 text-center border d-flex flex-column justify-content-center"
                      style={{
                        height: '70px',
                        cursor: recStop > 0 ? 'pointer' : 'default',
                        transition: 'all 0.15s',
                        backgroundColor: recStop > 0 ? '#fff7ed' : 'rgba(248, 249, 250, 0.5)',
                        borderColor: recStop > 0 ? '#fed7aa' : 'rgba(0,0,0,0.08)'
                      }}
                      onClick={() => recStop > 0 && handleNavigate('/work-order/received', 'stop')}
                      onMouseEnter={e => { if (recStop > 0) e.currentTarget.style.backgroundColor = '#ffedd5'; }}
                      onMouseLeave={e => { e.currentTarget.style.backgroundColor = recStop > 0 ? '#fff7ed' : 'rgba(248, 249, 250, 0.5)'; }}
                    >
                      <div className="fw-semibold mb-1 text-truncate" style={{ fontSize: '0.78rem', lineHeight: 1.2, color: recStop > 0 ? '#c2410c' : '#64748b' }}>
                        <span style={{ color: '#ea580c' }} className="me-1">●</span>중단
                      </div>
                      <div className={`fw-bold lh-1 ${recStop > 0 ? '' : 'text-muted'}`} style={{ fontSize: '0.85rem', color: recStop > 0 ? '#c2410c' : undefined }}>
                        {recStop}
                      </div>
                    </div>

                    {/* 2. 대기 */}
                    <div
                      className="rounded-2 text-center border d-flex flex-column justify-content-center"
                      style={{
                        height: '70px',
                        cursor: recRequest > 0 ? 'pointer' : 'default',
                        transition: 'all 0.15s',
                        backgroundColor: recRequest > 0 ? '#fffbeb' : 'rgba(248, 249, 250, 0.5)',
                        borderColor: recRequest > 0 ? '#fde68a' : 'rgba(0,0,0,0.08)'
                      }}
                      onClick={() => recRequest > 0 && handleNavigate('/work-order/received', 'request')}
                      onMouseEnter={e => { if (recRequest > 0) e.currentTarget.style.backgroundColor = '#fef3c7'; }}
                      onMouseLeave={e => { e.currentTarget.style.backgroundColor = recRequest > 0 ? '#fffbeb' : 'rgba(248, 249, 250, 0.5)'; }}
                    >
                      <div className="fw-semibold mb-1 text-truncate" style={{ fontSize: '0.78rem', lineHeight: 1.2, color: recRequest > 0 ? '#b45309' : '#64748b' }}>
                        <span style={{ color: '#f59e0b' }} className="me-1">●</span>대기(미접수)
                      </div>
                      <div className={`fw-bold lh-1 ${recRequest > 0 ? '' : 'text-muted'}`} style={{ fontSize: '0.85rem', color: recRequest > 0 ? '#b45309' : undefined }}>
                        {recRequest}
                      </div>
                    </div>

                    {/* 3. 진행중 */}
                    <div
                      className="rounded-2 text-center border d-flex flex-column justify-content-center"
                      style={{
                        height: '70px',
                        cursor: recProgress > 0 ? 'pointer' : 'default',
                        transition: 'all 0.15s',
                        backgroundColor: recProgress > 0 ? '#eff6ff' : 'rgba(248, 249, 250, 0.5)',
                        borderColor: recProgress > 0 ? '#bfdbfe' : 'rgba(0,0,0,0.08)'
                      }}
                      onClick={() => recProgress > 0 && handleNavigate('/work-order/received', 'progress')}
                      onMouseEnter={e => { if (recProgress > 0) e.currentTarget.style.backgroundColor = '#dbeafe'; }}
                      onMouseLeave={e => { e.currentTarget.style.backgroundColor = recProgress > 0 ? '#eff6ff' : 'rgba(248, 249, 250, 0.5)'; }}
                    >
                      <div className="fw-semibold mb-1 text-truncate" style={{ fontSize: '0.78rem', lineHeight: 1.2, color: recProgress > 0 ? '#1d4ed8' : '#64748b' }}>
                        <span style={{ color: '#2563eb' }} className="me-1">●</span>진행중
                      </div>
                      <div className={`fw-bold lh-1 ${recProgress > 0 ? '' : 'text-muted'}`} style={{ fontSize: '0.85rem', color: recProgress > 0 ? '#1d4ed8' : undefined }}>
                        {recProgress}
                      </div>
                    </div>

                    {/* 4. 보고/검토 */}
                    <div
                      className="rounded-2 text-center border d-flex flex-column justify-content-center"
                      style={{
                        height: '70px',
                        cursor: recReview > 0 ? 'pointer' : 'default',
                        transition: 'all 0.15s',
                        backgroundColor: recReview > 0 ? '#f5f3ff' : 'rgba(248, 249, 250, 0.5)',
                        borderColor: recReview > 0 ? '#ddd6fe' : 'rgba(0,0,0,0.08)'
                      }}
                      onClick={() => recReview > 0 && handleNavigate('/work-order/received', 'review')}
                      onMouseEnter={e => { if (recReview > 0) e.currentTarget.style.backgroundColor = '#ede9fe'; }}
                      onMouseLeave={e => { e.currentTarget.style.backgroundColor = recReview > 0 ? '#f5f3ff' : 'rgba(248, 249, 250, 0.5)'; }}
                    >
                      <div className="fw-semibold mb-1 text-truncate" style={{ fontSize: '0.78rem', lineHeight: 1.2, color: recReview > 0 ? '#6b21a8' : '#64748b' }}>
                        <span style={{ color: '#8b5cf6' }} className="me-1">●</span>결과/검토
                      </div>
                      <div className={`fw-bold lh-1 ${recReview > 0 ? '' : 'text-muted'}`} style={{ fontSize: '0.85rem', color: recReview > 0 ? '#6b21a8' : undefined }}>
                        {recReview}
                      </div>
                    </div>

                    {/* 5. 지연 */}
                    <div
                      className="rounded-2 text-center border d-flex flex-column justify-content-center"
                      style={{
                        height: '70px',
                        cursor: recDelay > 0 ? 'pointer' : 'default',
                        transition: 'all 0.15s',
                        backgroundColor: recDelay > 0 ? '#fef2f2' : 'rgba(248, 249, 250, 0.5)',
                        borderColor: recDelay > 0 ? '#fecaca' : 'rgba(0,0,0,0.08)'
                      }}
                      onClick={() => recDelay > 0 && handleNavigate('/work-order/received', 'delay')}
                      onMouseEnter={e => { if (recDelay > 0) e.currentTarget.style.backgroundColor = '#fee2e2'; }}
                      onMouseLeave={e => { e.currentTarget.style.backgroundColor = recDelay > 0 ? '#fef2f2' : 'rgba(248, 249, 250, 0.5)'; }}
                    >
                      <div className="fw-semibold mb-1 text-truncate" style={{ fontSize: '0.78rem', lineHeight: 1.2, color: recDelay > 0 ? '#b91c1c' : '#64748b' }}>
                        <span style={{ color: '#ef4444' }} className="me-1">●</span>지연
                      </div>
                      <div className={`fw-bold lh-1 ${recDelay > 0 ? '' : 'text-muted'}`} style={{ fontSize: '0.85rem', color: recDelay > 0 ? '#b91c1c' : undefined }}>
                        {recDelay}
                      </div>
                      {recDelay > 0 && (
                        <div style={{ fontSize: '0.67rem', color: '#dc2626', marginTop: '3px', fontWeight: 500, lineHeight: 1 }}>
                          (접수 {recDelayRequest} / 진행 {recDelayProgress})
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* ── 위젯 2: 지시내린 업무 (내가 지시자) ── */}
            <div className="col-12 col-xl-6">
              <div
                className="card border-0 shadow-sm rounded-3 overflow-hidden"
                style={{ borderTop: '3px solid #8b5cf6' }}
              >
                <div
                  className="card-header border-bottom d-flex justify-content-between align-items-center"
                  style={{
                    background: 'linear-gradient(135deg, #f5f3ff 0%, #ede9fe 100%)',
                    borderBottom: '1px solid #ddd6fe',
                    padding: '12px 18px'
                  }}
                >
                  <div className="d-flex align-items-center gap-3">
                    <div
                      className="rounded-circle d-flex align-items-center justify-content-center shadow-sm text-white flex-shrink-0"
                      style={{ width: '36px', height: '36px', background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)' }}
                    >
                      <i className="fas fa-paper-plane fs-6" />
                    </div>
                    <div className="d-flex flex-column justify-content-center">
                      <div className="fw-bold" style={{ fontSize: '0.92rem', color: '#6b21a8', lineHeight: 1.2, marginBottom: '2px' }}>요청한 업무</div>
                      <div className="small" style={{ fontSize: '0.75rem', color: '#7c3aed', lineHeight: 1 }}>내가 요청하여 담당자가 수행 중인 협조 업무</div>
                    </div>
                  </div>
                  <div className="d-flex align-items-center gap-2">
                    <div className="bg-white rounded-pill px-3 py-1 shadow-sm d-flex align-items-center gap-1 border" style={{ borderColor: '#ddd6fe' }}>
                      <span className="fw-bold" style={{ color: '#7c3aed', fontSize: '0.78rem' }}>{sentTotal}</span>
                      <span className="text-secondary fw-medium" style={{ fontSize: '0.78rem' }}>건</span>
                    </div>
                    <button
                      className="btn btn-sm py-1 px-3 ms-1 shadow-sm rounded-pill text-white fw-medium"
                      style={{ fontSize: '0.75rem', backgroundColor: '#7c3aed', borderColor: '#7c3aed' }}
                      onClick={() => handleNavigate('/work-order/sent')}
                    >
                      목록 <i className="fas fa-chevron-right ms-1" />
                    </button>
                  </div>
                </div>

                <div className="card-body" style={{ padding: '14px 16px' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(88px, 1fr))', gap: '8px' }}>
                    {/* 1. 중단 */}
                    <div
                      className="rounded-2 text-center border d-flex flex-column justify-content-center"
                      style={{
                        height: '70px',
                        cursor: sentStop > 0 ? 'pointer' : 'default',
                        transition: 'all 0.15s',
                        backgroundColor: sentStop > 0 ? '#fff7ed' : 'rgba(248, 249, 250, 0.5)',
                        borderColor: sentStop > 0 ? '#fed7aa' : 'rgba(0,0,0,0.08)'
                      }}
                      onClick={() => sentStop > 0 && handleNavigate('/work-order/sent', 'stop')}
                      onMouseEnter={e => { if (sentStop > 0) e.currentTarget.style.backgroundColor = '#ffedd5'; }}
                      onMouseLeave={e => { e.currentTarget.style.backgroundColor = sentStop > 0 ? '#fff7ed' : 'rgba(248, 249, 250, 0.5)'; }}
                    >
                      <div className="fw-semibold mb-1 text-truncate" style={{ fontSize: '0.78rem', lineHeight: 1.2, color: sentStop > 0 ? '#c2410c' : '#64748b' }}>
                        <span style={{ color: '#ea580c' }} className="me-1">●</span>중단
                      </div>
                      <div className={`fw-bold lh-1 ${sentStop > 0 ? '' : 'text-muted'}`} style={{ fontSize: '0.85rem', color: sentStop > 0 ? '#c2410c' : undefined }}>
                        {sentStop}
                      </div>
                    </div>

                    {/* 2. 대기 */}
                    <div
                      className="rounded-2 text-center border d-flex flex-column justify-content-center"
                      style={{
                        height: '70px',
                        cursor: sentRequest > 0 ? 'pointer' : 'default',
                        transition: 'all 0.15s',
                        backgroundColor: sentRequest > 0 ? '#fffbeb' : 'rgba(248, 249, 250, 0.5)',
                        borderColor: sentRequest > 0 ? '#fde68a' : 'rgba(0,0,0,0.08)'
                      }}
                      onClick={() => sentRequest > 0 && handleNavigate('/work-order/sent', 'request')}
                      onMouseEnter={e => { if (sentRequest > 0) e.currentTarget.style.backgroundColor = '#fef3c7'; }}
                      onMouseLeave={e => { e.currentTarget.style.backgroundColor = sentRequest > 0 ? '#fffbeb' : 'rgba(248, 249, 250, 0.5)'; }}
                    >
                      <div className="fw-semibold mb-1 text-truncate" style={{ fontSize: '0.78rem', lineHeight: 1.2, color: sentRequest > 0 ? '#b45309' : '#64748b' }}>
                        <span style={{ color: '#f59e0b' }} className="me-1">●</span>대기(미접수)
                      </div>
                      <div className={`fw-bold lh-1 ${sentRequest > 0 ? '' : 'text-muted'}`} style={{ fontSize: '0.85rem', color: sentRequest > 0 ? '#b45309' : undefined }}>
                        {sentRequest}
                      </div>
                    </div>

                    {/* 3. 진행중 */}
                    <div
                      className="rounded-2 text-center border d-flex flex-column justify-content-center"
                      style={{
                        height: '70px',
                        cursor: sentProgress > 0 ? 'pointer' : 'default',
                        transition: 'all 0.15s',
                        backgroundColor: sentProgress > 0 ? '#eff6ff' : 'rgba(248, 249, 250, 0.5)',
                        borderColor: sentProgress > 0 ? '#bfdbfe' : 'rgba(0,0,0,0.08)'
                      }}
                      onClick={() => sentProgress > 0 && handleNavigate('/work-order/sent', 'progress')}
                      onMouseEnter={e => { if (sentProgress > 0) e.currentTarget.style.backgroundColor = '#dbeafe'; }}
                      onMouseLeave={e => { e.currentTarget.style.backgroundColor = sentProgress > 0 ? '#eff6ff' : 'rgba(248, 249, 250, 0.5)'; }}
                    >
                      <div className="fw-semibold mb-1 text-truncate" style={{ fontSize: '0.78rem', lineHeight: 1.2, color: sentProgress > 0 ? '#1d4ed8' : '#64748b' }}>
                        <span style={{ color: '#2563eb' }} className="me-1">●</span>진행중
                      </div>
                      <div className={`fw-bold lh-1 ${sentProgress > 0 ? '' : 'text-muted'}`} style={{ fontSize: '0.85rem', color: sentProgress > 0 ? '#1d4ed8' : undefined }}>
                        {sentProgress}
                      </div>
                    </div>

                    {/* 4. 검토 대기 */}
                    <div
                      className="rounded-2 text-center border d-flex flex-column justify-content-center"
                      style={{
                        height: '70px',
                        cursor: sentReview > 0 ? 'pointer' : 'default',
                        transition: 'all 0.15s',
                        backgroundColor: sentReview > 0 ? '#f5f3ff' : 'rgba(248, 249, 250, 0.5)',
                        borderColor: sentReview > 0 ? '#ddd6fe' : 'rgba(0,0,0,0.08)'
                      }}
                      onClick={() => sentReview > 0 && handleNavigate('/work-order/sent', 'review')}
                      onMouseEnter={e => { if (sentReview > 0) e.currentTarget.style.backgroundColor = '#ede9fe'; }}
                      onMouseLeave={e => { e.currentTarget.style.backgroundColor = sentReview > 0 ? '#f5f3ff' : 'rgba(248, 249, 250, 0.5)'; }}
                    >
                      <div className="fw-semibold mb-1 text-truncate" style={{ fontSize: '0.78rem', lineHeight: 1.2, color: sentReview > 0 ? '#6b21a8' : '#64748b' }}>
                        <span style={{ color: '#8b5cf6' }} className="me-1">●</span>검토 대기
                      </div>
                      <div className={`fw-bold lh-1 ${sentReview > 0 ? '' : 'text-muted'}`} style={{ fontSize: '0.85rem', color: sentReview > 0 ? '#6b21a8' : undefined }}>
                        {sentReview}
                      </div>
                    </div>

                    {/* 5. 지연 */}
                    <div
                      className="rounded-2 text-center border d-flex flex-column justify-content-center"
                      style={{
                        height: '70px',
                        cursor: sentDelay > 0 ? 'pointer' : 'default',
                        transition: 'all 0.15s',
                        backgroundColor: sentDelay > 0 ? '#fef2f2' : 'rgba(248, 249, 250, 0.5)',
                        borderColor: sentDelay > 0 ? '#fecaca' : 'rgba(0,0,0,0.08)'
                      }}
                      onClick={() => sentDelay > 0 && handleNavigate('/work-order/sent', 'delay')}
                      onMouseEnter={e => { if (sentDelay > 0) e.currentTarget.style.backgroundColor = '#fee2e2'; }}
                      onMouseLeave={e => { e.currentTarget.style.backgroundColor = sentDelay > 0 ? '#fef2f2' : 'rgba(248, 249, 250, 0.5)'; }}
                    >
                      <div className="fw-semibold mb-1 text-truncate" style={{ fontSize: '0.78rem', lineHeight: 1.2, color: sentDelay > 0 ? '#b91c1c' : '#64748b' }}>
                        <span style={{ color: '#ef4444' }} className="me-1">●</span>지연
                      </div>
                      <div className={`fw-bold lh-1 ${sentDelay > 0 ? '' : 'text-muted'}`} style={{ fontSize: '0.85rem', color: sentDelay > 0 ? '#b91c1c' : undefined }}>
                        {sentDelay}
                      </div>
                      {sentDelay > 0 && (
                        <div style={{ fontSize: '0.67rem', color: '#dc2626', marginTop: '3px', fontWeight: 500, lineHeight: 1 }}>
                          (접수 {sentDelayRequest} / 진행 {sentDelayProgress})
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* ── 위젯 3: 참조 업무 (내가 참조자) ── */}
            <div className="col-12 col-xl-6">
              <div
                className="card border-0 shadow-sm rounded-3 overflow-hidden"
                style={{ borderTop: '3px solid #06b6d4' }}
              >
                <div
                  className="card-header border-bottom d-flex justify-content-between align-items-center"
                  style={{
                    background: 'linear-gradient(135deg, #ecfeff 0%, #cffafe 100%)',
                    borderBottom: '1px solid #a5f3fc',
                    padding: '12px 18px'
                  }}
                >
                  <div className="d-flex align-items-center gap-3">
                    <div
                      className="rounded-circle d-flex align-items-center justify-content-center shadow-sm text-white flex-shrink-0"
                      style={{ width: '36px', height: '36px', background: 'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)' }}
                    >
                      <i className="fas fa-eye fs-6" />
                    </div>
                    <div className="d-flex flex-column justify-content-center">
                      <div className="fw-bold" style={{ fontSize: '0.92rem', color: '#0e7490', lineHeight: 1.2, marginBottom: '2px' }}>참조 업무</div>
                      <div className="small" style={{ fontSize: '0.75rem', color: '#0891b2', lineHeight: 1 }}>업무 협조 및 진행 경과를 공유받은 업무</div>
                    </div>
                  </div>
                  <div className="d-flex align-items-center gap-2">
                    <div className="bg-white rounded-pill px-3 py-1 shadow-sm d-flex align-items-center gap-1 border" style={{ borderColor: '#a5f3fc' }}>
                      <span className="fw-bold" style={{ color: '#0891b2', fontSize: '0.78rem' }}>{refTotal}</span>
                      <span className="text-secondary fw-medium" style={{ fontSize: '0.78rem' }}>건</span>
                    </div>
                    <button
                      className="btn btn-sm py-1 px-3 ms-1 shadow-sm rounded-pill text-white fw-medium"
                      style={{ fontSize: '0.75rem', backgroundColor: '#0891b2', borderColor: '#0891b2' }}
                      onClick={() => handleNavigate('/work-order/referred')}
                    >
                      목록 <i className="fas fa-chevron-right ms-1" />
                    </button>
                  </div>
                </div>

                <div className="card-body" style={{ padding: '14px 16px' }}>
                  <div className="row g-2">
                    {/* 1. 중단 */}
                    <div className="col-3">
                      <div
                        className="rounded-2 text-center border d-flex flex-column justify-content-center"
                        style={{
                          height: '70px',
                          cursor: refStop > 0 ? 'pointer' : 'default',
                          transition: 'all 0.15s',
                          backgroundColor: refStop > 0 ? '#fff7ed' : 'rgba(248, 249, 250, 0.5)',
                          borderColor: refStop > 0 ? '#fed7aa' : 'rgba(0,0,0,0.08)'
                        }}
                        onClick={() => refStop > 0 && handleNavigate('/work-order/referred', 'stop')}
                        onMouseEnter={e => { if (refStop > 0) e.currentTarget.style.backgroundColor = '#ffedd5'; }}
                        onMouseLeave={e => { e.currentTarget.style.backgroundColor = refStop > 0 ? '#fff7ed' : 'rgba(248, 249, 250, 0.5)'; }}
                      >
                        <div className="fw-semibold mb-1 text-truncate" style={{ fontSize: '0.78rem', lineHeight: 1.2, color: refStop > 0 ? '#c2410c' : '#64748b' }}>
                          <span style={{ color: '#ea580c' }} className="me-1">●</span>중단
                        </div>
                        <div className={`fw-bold lh-1 ${refStop > 0 ? '' : 'text-muted'}`} style={{ fontSize: '0.85rem', color: refStop > 0 ? '#c2410c' : undefined }}>
                          {refStop}
                        </div>
                      </div>
                    </div>

                    {/* 2. 진행중 */}
                    <div className="col-3">
                      <div
                        className="rounded-2 text-center border d-flex flex-column justify-content-center"
                        style={{
                          height: '70px',
                          cursor: refProgress > 0 ? 'pointer' : 'default',
                          transition: 'all 0.15s',
                          backgroundColor: refProgress > 0 ? '#ecfeff' : 'rgba(248, 249, 250, 0.5)',
                          borderColor: refProgress > 0 ? '#a5f3fc' : 'rgba(0,0,0,0.08)'
                        }}
                        onClick={() => refProgress > 0 && handleNavigate('/work-order/referred', 'progress')}
                        onMouseEnter={e => { if (refProgress > 0) e.currentTarget.style.backgroundColor = '#cffafe'; }}
                        onMouseLeave={e => { e.currentTarget.style.backgroundColor = refProgress > 0 ? '#ecfeff' : 'rgba(248, 249, 250, 0.5)'; }}
                      >
                        <div className="fw-semibold mb-1 text-truncate" style={{ fontSize: '0.78rem', lineHeight: 1.2, color: refProgress > 0 ? '#0e7490' : '#64748b' }}>
                          <span style={{ color: '#06b6d4' }} className="me-1">●</span>진행중
                        </div>
                        <div className={`fw-bold lh-1 ${refProgress > 0 ? '' : 'text-muted'}`} style={{ fontSize: '0.85rem', color: refProgress > 0 ? '#0e7490' : undefined }}>
                          {refProgress}
                        </div>
                      </div>
                    </div>

                    {/* 3. 지연 */}
                    <div className="col-3">
                      <div
                        className="rounded-2 text-center border d-flex flex-column justify-content-center"
                        style={{
                          height: '70px',
                          cursor: refDelay > 0 ? 'pointer' : 'default',
                          transition: 'all 0.15s',
                          backgroundColor: refDelay > 0 ? '#fef2f2' : 'rgba(248, 249, 250, 0.5)',
                          borderColor: refDelay > 0 ? '#fecaca' : 'rgba(0,0,0,0.08)'
                        }}
                        onClick={() => refDelay > 0 && handleNavigate('/work-order/referred', 'delay')}
                        onMouseEnter={e => { if (refDelay > 0) e.currentTarget.style.backgroundColor = '#fee2e2'; }}
                        onMouseLeave={e => { e.currentTarget.style.backgroundColor = refDelay > 0 ? '#fef2f2' : 'rgba(248, 249, 250, 0.5)'; }}
                      >
                        <div className="fw-semibold mb-1 text-truncate" style={{ fontSize: '0.78rem', lineHeight: 1.2, color: refDelay > 0 ? '#b91c1c' : '#64748b' }}>
                          <span style={{ color: '#ef4444' }} className="me-1">●</span>지연
                        </div>
                        <div className={`fw-bold lh-1 ${refDelay > 0 ? '' : 'text-muted'}`} style={{ fontSize: '0.85rem', color: refDelay > 0 ? '#b91c1c' : undefined }}>
                          {refDelay}
                        </div>
                      </div>
                    </div>

                    {/* 4. 완료 */}
                    <div className="col-3">
                      <div
                        className="rounded-2 text-center border d-flex flex-column justify-content-center"
                        style={{
                          height: '70px',
                          cursor: refDone > 0 ? 'pointer' : 'default',
                          transition: 'all 0.15s',
                          backgroundColor: refDone > 0 ? '#f0fdf4' : 'rgba(248, 249, 250, 0.5)',
                          borderColor: refDone > 0 ? '#bbf7d0' : 'rgba(0,0,0,0.08)'
                        }}
                        onClick={() => refDone > 0 && handleNavigate('/work-order/completed', 'referred')}
                        onMouseEnter={e => { if (refDone > 0) e.currentTarget.style.backgroundColor = '#dcfce7'; }}
                        onMouseLeave={e => { e.currentTarget.style.backgroundColor = refDone > 0 ? '#f0fdf4' : 'rgba(248, 249, 250, 0.5)'; }}
                      >
                        <div className="fw-semibold mb-1 text-truncate" style={{ fontSize: '0.78rem', lineHeight: 1.2, color: refDone > 0 ? '#15803d' : '#64748b' }}>
                          <span style={{ color: '#10b981' }} className="me-1">●</span>완료됨
                        </div>
                        <div className={`fw-bold lh-1 ${refDone > 0 ? '' : 'text-muted'}`} style={{ fontSize: '0.85rem', color: refDone > 0 ? '#15803d' : undefined }}>
                          {refDone}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* ── 위젯 4: 완료된 업무 (종결 히스토리) ── */}
            <div className="col-12 col-xl-6">
              <div
                className="card border-0 shadow-sm rounded-3 overflow-hidden"
                style={{ borderTop: '3px solid #10b981' }}
              >
                <div
                  className="card-header border-bottom d-flex justify-content-between align-items-center"
                  style={{
                    background: 'linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)',
                    borderBottom: '1px solid #a7f3d0',
                    padding: '12px 18px'
                  }}
                >
                  <div className="d-flex align-items-center gap-3">
                    <div
                      className="rounded-circle d-flex align-items-center justify-content-center shadow-sm text-white flex-shrink-0"
                      style={{ width: '36px', height: '36px', background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)' }}
                    >
                      <i className="fas fa-check-circle fs-6" />
                    </div>
                    <div className="d-flex flex-column justify-content-center">
                      <div className="fw-bold" style={{ fontSize: '0.92rem', color: '#065f46', lineHeight: 1.2, marginBottom: '2px' }}>완료된 업무</div>
                      <div className="small" style={{ fontSize: '0.75rem', color: '#059669', lineHeight: 1 }}>처리가 완료되어 최종 확인 종결된 업무</div>
                    </div>
                  </div>
                  <div className="d-flex align-items-center gap-2">
                    <div className="bg-white rounded-pill px-3 py-1 shadow-sm d-flex align-items-center gap-1 border" style={{ borderColor: '#a7f3d0' }}>
                      <span className="fw-bold text-success" style={{ fontSize: '0.78rem' }}>{compTotal}</span>
                      <span className="text-secondary fw-medium" style={{ fontSize: '0.78rem' }}>건</span>
                    </div>
                    <button
                      className="btn btn-success btn-sm py-1 px-3 ms-1 shadow-sm rounded-pill fw-medium"
                      style={{ fontSize: '0.75rem', backgroundColor: '#059669', borderColor: '#059669' }}
                      onClick={() => handleNavigate('/work-order/completed')}
                    >
                      목록 <i className="fas fa-chevron-right ms-1" />
                    </button>
                  </div>
                </div>

                <div className="card-body" style={{ padding: '14px 16px' }}>
                  <div className="row g-2">
                    {/* 내가 받은 완료 (파랑) */}
                    <div className="col-4">
                      <div
                        className="rounded-2 text-center border d-flex flex-column justify-content-center"
                        style={{
                          height: '70px',
                          cursor: recDone > 0 ? 'pointer' : 'default',
                          transition: 'all 0.15s',
                          backgroundColor: recDone > 0 ? '#eff6ff' : 'rgba(248, 249, 250, 0.5)',
                          borderColor: recDone > 0 ? '#bfdbfe' : 'rgba(0,0,0,0.08)'
                        }}
                        onClick={() => recDone > 0 && handleNavigate('/work-order/completed', 'received')}
                        onMouseEnter={e => { if (recDone > 0) e.currentTarget.style.backgroundColor = '#dbeafe'; }}
                        onMouseLeave={e => { e.currentTarget.style.backgroundColor = recDone > 0 ? '#eff6ff' : 'rgba(248, 249, 250, 0.5)'; }}
                      >
                        <div className="fw-semibold mb-1 text-truncate" style={{ fontSize: '0.78rem', lineHeight: 1.2, color: recDone > 0 ? '#1d4ed8' : '#64748b' }}>
                          <span style={{ color: '#2563eb' }} className="me-1">●</span>요청받은 업무
                        </div>
                        <div className={`fw-bold lh-1 ${recDone > 0 ? '' : 'text-muted'}`} style={{ fontSize: '0.85rem', color: recDone > 0 ? '#1d4ed8' : undefined }}>
                          {recDone}
                        </div>
                      </div>
                    </div>

                    {/* 내가 지시한 완료 (보라) */}
                    <div className="col-4">
                      <div
                        className="rounded-2 text-center border d-flex flex-column justify-content-center"
                        style={{
                          height: '70px',
                          cursor: sentDone > 0 ? 'pointer' : 'default',
                          transition: 'all 0.15s',
                          backgroundColor: sentDone > 0 ? '#f5f3ff' : 'rgba(248, 249, 250, 0.5)',
                          borderColor: sentDone > 0 ? '#ddd6fe' : 'rgba(0,0,0,0.08)'
                        }}
                        onClick={() => sentDone > 0 && handleNavigate('/work-order/completed', 'sent')}
                        onMouseEnter={e => { if (sentDone > 0) e.currentTarget.style.backgroundColor = '#ede9fe'; }}
                        onMouseLeave={e => { e.currentTarget.style.backgroundColor = sentDone > 0 ? '#f5f3ff' : 'rgba(248, 249, 250, 0.5)'; }}
                      >
                        <div className="fw-semibold mb-1 text-truncate" style={{ fontSize: '0.78rem', lineHeight: 1.2, color: sentDone > 0 ? '#6b21a8' : '#64748b' }}>
                          <span style={{ color: '#8b5cf6' }} className="me-1">●</span>요청한 업무
                        </div>
                        <div className={`fw-bold lh-1 ${sentDone > 0 ? '' : 'text-muted'}`} style={{ fontSize: '0.85rem', color: sentDone > 0 ? '#6b21a8' : undefined }}>
                          {sentDone}
                        </div>
                      </div>
                    </div>

                    {/* 참조 완료 (청록) */}
                    <div className="col-4">
                      <div
                        className="rounded-2 text-center border d-flex flex-column justify-content-center"
                        style={{
                          height: '70px',
                          cursor: refDone > 0 ? 'pointer' : 'default',
                          transition: 'all 0.15s',
                          backgroundColor: refDone > 0 ? '#ecfeff' : 'rgba(248, 249, 250, 0.5)',
                          borderColor: refDone > 0 ? '#a5f3fc' : 'rgba(0,0,0,0.08)'
                        }}
                        onClick={() => refDone > 0 && handleNavigate('/work-order/completed', 'referred')}
                        onMouseEnter={e => { if (refDone > 0) e.currentTarget.style.backgroundColor = '#cffafe'; }}
                        onMouseLeave={e => { e.currentTarget.style.backgroundColor = refDone > 0 ? '#ecfeff' : 'rgba(248, 249, 250, 0.5)'; }}
                      >
                        <div className="fw-semibold mb-1 text-truncate" style={{ fontSize: '0.78rem', lineHeight: 1.2, color: refDone > 0 ? '#0e7490' : '#64748b' }}>
                          <span style={{ color: '#06b6d4' }} className="me-1">●</span>참조 업무
                        </div>
                        <div className={`fw-bold lh-1 ${refDone > 0 ? '' : 'text-muted'}`} style={{ fontSize: '0.85rem', color: refDone > 0 ? '#0e7490' : undefined }}>
                          {refDone}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* ── 3. 하단 섹션: 오늘 마감/지연 긴급 리스트 & 바로가기 ── */}
          <div className="d-flex flex-column flex-lg-row gap-3" style={{ marginTop: '0px' }}>
            {/* 좌측: 오늘 마감 및 지연 주의 업무 (넓게 확장) */}
            <div style={{ flex: '1 1 auto', minWidth: 0 }}>
              <div
                className="card border-0 shadow-sm rounded-3 h-100 overflow-hidden"
                style={{ borderTop: '3px solid #ef4444' }}
              >
                <div
                  className="card-header border-bottom d-flex justify-content-between align-items-center"
                  style={{
                    background: 'linear-gradient(135deg, #fff1f2 0%, #ffe4e6 100%)',
                    borderBottom: '1px solid #fecdd3',
                    padding: '12px 18px'
                  }}
                >
                  <div className="d-flex align-items-center gap-2 flex-wrap">
                    <div
                      className="rounded-circle d-flex align-items-center justify-content-center shadow-sm text-white flex-shrink-0"
                      style={{ width: '36px', height: '36px', background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)' }}
                    >
                      <i className="fas fa-bell fs-6" />
                    </div>
                    <div className="d-flex flex-column justify-content-center me-2">
                      <div className="fw-bold" style={{ fontSize: '0.92rem', color: '#991b1b', lineHeight: 1.2, marginBottom: '2px' }}>오늘 마감 및 지연 주의 업무</div>
                      <div className="small" style={{ fontSize: '0.78rem', color: '#dc2626', lineHeight: 1 }}>마감 임박 및 지연 긴급 조치 대상</div>
                    </div>
                    {/* [건수] 및 [기한 관리] 뱃지 (왼쪽 타이틀 옆으로 이동) */}
                    <div className="d-flex align-items-center gap-1.5 ms-1">
                      <div className="bg-white rounded-pill px-2.5 py-0.5 shadow-sm d-flex align-items-center gap-1 border" style={{ borderColor: '#fecdd3', height: '24px' }}>
                        <span className={`fw-bold ${urgent.length > 0 ? 'text-danger' : 'text-secondary'}`} style={{ fontSize: '0.78rem' }}>{urgent.length}</span>
                        <span className="text-secondary fw-medium" style={{ fontSize: '0.75rem' }}>건</span>
                      </div>
                      <span className="badge bg-white text-danger border border-danger-subtle rounded-pill px-2 py-1 fw-medium" style={{ fontSize: '0.72rem' }}>
                        기한 관리
                      </span>
                    </div>
                  </div>

                  {/* 우측: 페이징 컨트롤 (« ‹ [1] [2] › » 상시 노출) */}
                  {totalUrgentPages > 1 && (
                    <div className="d-flex align-items-center gap-1">
                      {/* « 맨 처음 페이지 */}
                      <button
                        type="button"
                        className="btn btn-sm p-0 d-flex align-items-center justify-content-center bg-white border"
                        disabled={urgentPage <= 1}
                        style={{
                          width: '28px',
                          height: '28px',
                          borderRadius: '6px',
                          borderColor: '#d1d5db',
                          color: '#64748b',
                          fontSize: '0.74rem',
                          boxShadow: '0 1px 2px rgba(0,0,0,0.03)',
                          cursor: urgentPage <= 1 ? 'not-allowed' : 'pointer',
                          opacity: urgentPage <= 1 ? 0.35 : 1
                        }}
                        onClick={() => urgentPage > 1 && setUrgentPage(1)}
                        title="처음으로"
                      >
                        <i className="fas fa-angle-double-left" />
                      </button>

                      {/* ‹ 이전 페이지 */}
                      <button
                        type="button"
                        className="btn btn-sm p-0 d-flex align-items-center justify-content-center bg-white border"
                        disabled={urgentPage <= 1}
                        style={{
                          width: '28px',
                          height: '28px',
                          borderRadius: '6px',
                          borderColor: '#d1d5db',
                          color: '#64748b',
                          fontSize: '0.74rem',
                          boxShadow: '0 1px 2px rgba(0,0,0,0.03)',
                          cursor: urgentPage <= 1 ? 'not-allowed' : 'pointer',
                          opacity: urgentPage <= 1 ? 0.35 : 1
                        }}
                        onClick={() => urgentPage > 1 && setUrgentPage(urgentPage - 1)}
                        title="이전 페이지"
                      >
                        <i className="fas fa-chevron-left" />
                      </button>

                      {/* [1] [2] ... 페이지 번호 버튼들 */}
                      {urgentPageNumbers.map(pageNum => {
                        const isActive = pageNum === urgentPage;
                        return (
                          <button
                            key={pageNum}
                            type="button"
                            className={`btn btn-sm p-0 d-flex align-items-center justify-content-center ${
                              isActive ? 'text-white' : 'bg-white text-secondary border'
                            }`}
                            style={{
                              width: '28px',
                              height: '28px',
                              borderRadius: '6px',
                              fontSize: '0.8rem',
                              fontWeight: 700,
                              background: isActive ? 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)' : '#ffffff',
                              borderColor: isActive ? '#dc2626' : '#d1d5db',
                              color: isActive ? '#ffffff' : '#475569',
                              boxShadow: isActive ? '0 2px 6px rgba(239, 68, 68, 0.35)' : '0 1px 2px rgba(0,0,0,0.03)'
                            }}
                            onClick={() => setUrgentPage(pageNum)}
                          >
                            {pageNum}
                          </button>
                        );
                      })}

                      {/* › 다음 페이지 */}
                      <button
                        type="button"
                        className="btn btn-sm p-0 d-flex align-items-center justify-content-center bg-white border"
                        disabled={urgentPage >= totalUrgentPages}
                        style={{
                          width: '28px',
                          height: '28px',
                          borderRadius: '6px',
                          borderColor: '#d1d5db',
                          color: '#64748b',
                          fontSize: '0.74rem',
                          boxShadow: '0 1px 2px rgba(0,0,0,0.03)',
                          cursor: urgentPage >= totalUrgentPages ? 'not-allowed' : 'pointer',
                          opacity: urgentPage >= totalUrgentPages ? 0.35 : 1
                        }}
                        onClick={() => urgentPage < totalUrgentPages && setUrgentPage(urgentPage + 1)}
                        title="다음 페이지"
                      >
                        <i className="fas fa-chevron-right" />
                      </button>

                      {/* » 맨 끝 페이지 */}
                      <button
                        type="button"
                        className="btn btn-sm p-0 d-flex align-items-center justify-content-center bg-white border"
                        disabled={urgentPage >= totalUrgentPages}
                        style={{
                          width: '28px',
                          height: '28px',
                          borderRadius: '6px',
                          borderColor: '#d1d5db',
                          color: '#64748b',
                          fontSize: '0.74rem',
                          boxShadow: '0 1px 2px rgba(0,0,0,0.03)',
                          cursor: urgentPage >= totalUrgentPages ? 'not-allowed' : 'pointer',
                          opacity: urgentPage >= totalUrgentPages ? 0.35 : 1
                        }}
                        onClick={() => urgentPage < totalUrgentPages && setUrgentPage(totalUrgentPages)}
                        title="마지막으로"
                      >
                        <i className="fas fa-angle-double-right" />
                      </button>
                    </div>
                  )}
                </div>

                <div className="card-body p-0">
                  {urgent.length === 0 ? (
                    <div className="text-center py-5 text-muted">
                      <div
                        className="rounded-circle d-inline-flex align-items-center justify-content-center mb-2"
                        style={{ width: '48px', height: '48px', backgroundColor: '#ecfdf5', color: '#10b981' }}
                      >
                        <i className="fas fa-check-circle fs-4" />
                      </div>
                      <p className="mb-0 fw-medium text-secondary" style={{ fontSize: '0.88rem' }}>현재 마감이 임박하거나 지연된 긴급 업무가 없습니다.</p>
                      <span className="text-muted" style={{ fontSize: '0.75rem' }}>모든 업무가 정상 일정으로 진행 중입니다.</span>
                    </div>
                  ) : (
                    <div className="table-responsive" style={{ overflowX: 'hidden' }}>
                      <table className="table table-hover align-middle mb-0" style={{ width: '100%', tableLayout: 'fixed' }}>
                        <thead className="table-light small">
                          <tr>
                            <th className="ps-3 fw-semibold text-secondary" style={{ width: '25%', fontSize: '0.8rem' }}>업무 제목</th>
                            <th className="text-center px-1 fw-semibold text-secondary" style={{ width: '13%', fontSize: '0.8rem' }}>요청자</th>
                            <th className="text-center px-1 fw-semibold text-secondary" style={{ width: '16%', fontSize: '0.8rem' }}>담당자</th>
                            <th className="text-center px-1 fw-semibold text-secondary" style={{ width: '12%', fontSize: '0.8rem' }}>마감일</th>
                            <th className="text-center px-1 fw-semibold text-secondary" style={{ width: '10%', fontSize: '0.8rem' }}>D-Day</th>
                            <th className="text-center px-1 fw-semibold text-secondary" style={{ width: '12%', fontSize: '0.8rem' }}>상태</th>
                            <th className="text-center px-1 fw-semibold text-secondary" style={{ width: '12%', fontSize: '0.8rem' }}>액션</th>
                          </tr>
                        </thead>
                        <tbody>
                          {urgent.slice((urgentPage - 1) * urgentPageSize, urgentPage * urgentPageSize).map(item => {
                            const dday = getDDay(item.due_date);
                            const statusInfo = getUrgentItemStatus(item);
                            return (
                              <tr
                                key={item.id}
                                style={{ cursor: 'pointer' }}
                                onClick={() => openViewModal(item.id)}
                              >
                                <td className="ps-3">
                                  <div className="fw-bold text-dark text-truncate" style={{ fontSize: '0.85rem' }} title={item.title}>
                                    {item.priority === 'urgent' && (
                                      <span className="badge bg-danger me-1" style={{ fontSize: '0.68rem' }}>긴급</span>
                                    )}
                                    {item.title}
                                  </div>
                                </td>
                                <td className="text-center px-1">
                                  {renderUrgentAuthor(item.author)}
                                </td>
                                <td className="text-center px-1">
                                  {renderUrgentAssignees(item.assignees)}
                                </td>
                                <td className="text-center px-1 text-secondary" style={{ fontSize: '0.82rem' }} title={item.due_date ? String(item.due_date).substring(0, 10) : '-'}>
                                  <span className="d-inline-block text-truncate" style={{ maxWidth: '100%' }}>
                                    {item.due_date ? String(item.due_date).substring(0, 10) : '-'}
                                  </span>
                                </td>
                                <td className="text-center px-1">
                                  {dday && <span className={`badge bg-light ${dday.cls} border px-2 py-1`} style={{ fontSize: '0.78rem' }}>{dday.text}</span>}
                                </td>
                                <td className="text-center px-1">
                                  <div
                                    style={{ display: 'inline-block', cursor: 'pointer' }}
                                    onMouseEnter={(e) => {
                                      e.stopPropagation();
                                      if (hoverStatusTimerRef.current) clearTimeout(hoverStatusTimerRef.current);
                                      const rect = e.currentTarget.getBoundingClientRect();
                                      const popoverWidth = 290;
                                      const spaceLeft = rect.left;
                                      const spaceRight = window.innerWidth - rect.right;
                                      const showRight = spaceLeft < popoverWidth && spaceRight >= spaceLeft;

                                      setHoveredStatus({
                                        label: statusInfo.label,
                                        delayInfo: statusInfo.delayInfo,
                                        badgeBg: statusInfo.bg,
                                        x: showRight ? (rect.right + 8) : (rect.left - 8),
                                        y: rect.top + rect.height / 2,
                                        badgeY: rect.top + rect.height / 2,
                                        showRight
                                      });
                                    }}
                                    onMouseLeave={() => {
                                      hoverStatusTimerRef.current = setTimeout(() => {
                                        setHoveredStatus(null);
                                      }, 180);
                                    }}
                                  >
                                    <span
                                      style={{
                                        display: 'inline-block',
                                        padding: '3px 9px',
                                        fontSize: '0.75rem',
                                        fontWeight: 600,
                                        borderRadius: '4px',
                                        backgroundColor: statusInfo.bg,
                                        color: statusInfo.color,
                                        border: `1px solid ${statusInfo.border}`,
                                        whiteSpace: 'nowrap'
                                      }}
                                    >
                                      {statusInfo.label}
                                    </span>
                                  </div>
                                </td>
                                <td className="text-center px-1" onClick={(e) => e.stopPropagation()}>
                                  {renderUrgentActions(item)}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* 우측: 주요 메뉴 바로가기 */}
            <div className="w-100" style={{ maxWidth: '305px', flexShrink: 0 }}>
              <div
                className="card border-0 shadow-sm rounded-3 h-100 overflow-hidden"
                style={{ borderTop: '3px solid #6366f1' }}
              >
                <div
                  className="card-header border-bottom d-flex justify-content-between align-items-start"
                  style={{
                    background: 'linear-gradient(135deg, #eef2ff 0%, #e0e7ff 100%)',
                    borderBottom: '1px solid #c7d2fe',
                    padding: '12px 15px'
                  }}
                >
                  <div className="d-flex align-items-center" style={{ gap: '12px' }}>
                    <div
                      className="rounded-circle d-flex align-items-center justify-content-center shadow-sm text-white flex-shrink-0"
                      style={{ width: '36px', height: '36px', background: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)' }}
                    >
                      <i className="fas fa-compass fs-6" />
                    </div>
                    <div className="d-flex flex-column justify-content-center">
                      <div className="fw-bold text-nowrap" style={{ fontSize: '0.92rem', color: '#3730a3', lineHeight: 1.2, marginBottom: '3px' }}>
                        주요 메뉴 바로가기
                      </div>
                      <div className="text-nowrap" style={{ fontSize: '0.75rem', color: '#4f46e5', lineHeight: 1.2 }}>
                        자주 사용하는 빠른 링크 모음
                      </div>
                    </div>
                  </div>
                  <span
                    className="badge bg-white border border-indigo-subtle rounded-pill px-2 py-0.5 fw-medium flex-shrink-0"
                    style={{ fontSize: '0.68rem', color: '#4f46e5', marginTop: '2px' }}
                  >
                    Quick Link
                  </span>
                </div>

                <div className="card-body p-3">
                  <div className="d-flex flex-column gap-2">
                    {[
                      { title: '신규 업무협조 작성', desc: '새로운 협조 작성 및 대상자 지정', onClick: openCreateModal, icon: 'fa-plus-circle', color: '#3b82f6' },
                      { title: '업무협조 타임라인', desc: '마감일 기준 스케줄 시각화 뷰', path: '/work-order/timeline', icon: 'fa-calendar-alt', color: '#8b5cf6' },
                      { title: '처리결과 목록', desc: '제출된 담당자 처리결과 검토 및 확인', path: '/work-order/reports', icon: 'fa-file-alt', color: '#10b981' },
                      { title: '임시저장 보관함', desc: '작성 중 임시 보관된 협조글', path: '/work-order/temp', icon: 'fa-save', color: '#f59e0b' },
                    ].map(menu => (
                      <div
                        key={menu.title}
                        className="d-flex align-items-center justify-content-between p-2 rounded-2 border border-light-subtle bg-light bg-opacity-50"
                        style={{ cursor: 'pointer', transition: 'all 0.15s ease' }}
                        onClick={() => {
                          if (menu.onClick) {
                            menu.onClick();
                          } else if (menu.path) {
                            navigate(menu.path);
                          }
                        }}
                        onMouseEnter={e => { e.currentTarget.style.backgroundColor = '#eef2ff'; }}
                        onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'rgba(248,249,250,0.5)'; }}
                      >
                        <div className="d-flex align-items-center gap-3">
                          <div
                            className="rounded-circle d-flex align-items-center justify-content-center shadow-sm"
                            style={{ width: '38px', height: '38px', backgroundColor: `${menu.color}15`, color: menu.color }}
                          >
                            <i className={`fas ${menu.icon} fs-6`} />
                          </div>
                          <div>
                            <div className="fw-bold text-dark" style={{ fontSize: '0.88rem' }}>{menu.title}</div>
                            <div className="text-secondary" style={{ fontSize: '0.75rem' }}>{menu.desc}</div>
                          </div>
                        </div>
                        <i className="fas fa-chevron-right text-muted small me-1" />
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── 3. 업무지시 등록 / 수정 / 조회 통합 모달 ── */}
      {modalOpen && modalMode === 'view' && (
        <WorkOrderDetailModal
          open={modalOpen}
          workOrderId={modalWorkOrderId}
          initialTab={modalInitialTab}
          onClose={closeModal}
          onStartEdit={(wo) => {
            setModalMode('edit');
          }}
          onDeleted={handleDeleted}
          onRefreshList={fetchMatrix}
        />
      )}

      {modalOpen && (modalMode === 'create' || modalMode === 'edit') && (
        <WorkOrderFormModal
          open={modalOpen}
          mode={modalMode}
          workOrderId={modalWorkOrderId}
          onClose={closeModal}
          onSaved={handleSaved}
          onDelete={handleDeleted}
          onDeleted={handleDeleted}
        />
      )}

      {/* ── 담당자 호버 팝오버 툴팁 (리스트 표준 100% 동일 + 스크롤 및 x버튼) ── */}
      {hoveredAttendees && createPortal(
        <div
          onMouseEnter={() => {
            if (hoverAttendeesTimerRef.current) clearTimeout(hoverAttendeesTimerRef.current);
          }}
          onMouseLeave={() => {
            hoverAttendeesTimerRef.current = setTimeout(() => {
              setHoveredAttendees(null);
            }, 180);
          }}
          style={{
            position: 'fixed',
            top: hoveredAttendees.y,
            left: hoveredAttendees.x,
            transform: hoveredAttendees.showDown ? 'translate(-50%, 0)' : 'translate(-50%, -100%)',
            backgroundColor: '#2d3748',
            borderRadius: '8px',
            padding: '12px',
            boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.5)',
            zIndex: 999999,
            width: 'max-content',
            maxWidth: '320px',
            pointerEvents: 'auto',
            overscrollBehavior: 'contain',
            color: '#fff'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', marginBottom: '8px' }}>
            <span className="badge" style={{ backgroundColor: '#2563eb', color: '#fff', fontSize: '0.75rem', padding: '4px 8px' }}>
              담당자 목록 ({hoveredAttendees.list.length}명)
            </span>
            <i
              className="fas fa-times"
              title="닫기"
              onClick={(e) => {
                e.stopPropagation();
                if (hoverAttendeesTimerRef.current) clearTimeout(hoverAttendeesTimerRef.current);
                setHoveredAttendees(null);
              }}
              style={{
                cursor: 'pointer',
                color: '#94a3b8',
                fontSize: '0.8rem',
                padding: '2px 4px',
                borderRadius: '4px',
                transition: 'color 0.15s ease, background-color 0.15s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.color = '#fff';
                e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.15)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.color = '#94a3b8';
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
            />
          </div>
          <div
            className="d-flex flex-wrap gap-2"
            style={{
              maxHeight: '180px',
              overflowY: 'auto',
              overscrollBehavior: 'contain',
              paddingRight: '4px'
            }}
          >
            {hoveredAttendees.list.map((name, i) => (
              <span key={i} className="badge" style={{ backgroundColor: '#4a5568', color: '#fff', fontSize: '0.8rem', padding: '6px 10px', fontWeight: 'normal' }}>
                {name}
              </span>
            ))}
          </div>
          <div style={{
            position: 'absolute',
            left: '50%',
            marginLeft: '-6px',
            top: hoveredAttendees.showDown ? '-6px' : '100%',
            borderWidth: hoveredAttendees.showDown ? '0 6px 6px 6px' : '6px 6px 0 6px',
            borderStyle: 'solid',
            borderColor: hoveredAttendees.showDown
              ? 'transparent transparent #2d3748 transparent'
              : '#2d3748 transparent transparent transparent'
          }} />
        </div>,
        document.body
      )}

      {/* ── 상태(접수지연/진행지연) 호버 팝오버 툴팁 (리스트 표준 100% 동일 + 좌측 표시) ── */}
      {hoveredStatus && (() => {
        const rawY = hoveredStatus.badgeY ?? hoveredStatus.y ?? 0;
        const spaceBottom = window.innerHeight - rawY;
        const spaceTop = rawY;
        const isNearBottom = spaceBottom < 190;
        const isNearTop = !isNearBottom && spaceTop < 120;

        let containerPosStyle = {};
        if (isNearBottom) {
          containerPosStyle = {
            bottom: Math.max(10, window.innerHeight - rawY - 14),
            transform: hoveredStatus.showRight ? 'translate(0, 0)' : 'translate(-100%, 0)'
          };
        } else if (isNearTop) {
          containerPosStyle = {
            top: Math.max(10, rawY - 14),
            transform: hoveredStatus.showRight ? 'translate(0, 0)' : 'translate(-100%, 0)'
          };
        } else {
          containerPosStyle = {
            top: rawY,
            transform: hoveredStatus.showRight ? 'translate(0, -50%)' : 'translate(-100%, -50%)'
          };
        }

        return createPortal(
          <div
            onMouseEnter={() => {
              if (hoverStatusTimerRef.current) clearTimeout(hoverStatusTimerRef.current);
            }}
            onMouseLeave={() => {
              hoverStatusTimerRef.current = setTimeout(() => {
                setHoveredStatus(null);
              }, 180);
            }}
            style={{
              position: 'fixed',
              left: hoveredStatus.x,
              ...containerPosStyle,
              backgroundColor: '#2d3748',
              borderRadius: '8px',
              padding: '10px 12px',
              boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.5)',
              zIndex: 999999,
              width: 'max-content',
              maxWidth: '280px',
              pointerEvents: 'auto',
              overscrollBehavior: 'contain',
              color: '#fff',
              fontSize: '0.75rem',
              lineHeight: '1.4'
            }}
          >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '12px',
              marginBottom: hoveredStatus.delayInfo?.type === 'delay' ? '8px' : '0'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
              <span
                style={{
                  height: '20px',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: hoveredStatus.label?.includes('접수') ? '#ef4444' : (hoveredStatus.label?.includes('지연') ? '#f97316' : (hoveredStatus.badgeBg || '#3b82f6')),
                  color: '#fff',
                  fontSize: '0.7rem',
                  fontWeight: 600,
                  padding: '0 7px',
                  borderRadius: '4px',
                  lineHeight: 1
                }}
              >
                {hoveredStatus.delayInfo?.title || hoveredStatus.label}
              </span>
            </div>
            <i
              className="fas fa-times"
              title="닫기"
              onClick={(e) => {
                e.stopPropagation();
                if (hoverStatusTimerRef.current) clearTimeout(hoverStatusTimerRef.current);
                setHoveredStatus(null);
              }}
              style={{
                cursor: 'pointer',
                color: '#94a3b8',
                fontSize: '0.8rem',
                padding: '2px 4px',
                borderRadius: '4px',
                transition: 'color 0.15s ease, background-color 0.15s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.color = '#fff';
                e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.15)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.color = '#94a3b8';
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
            />
          </div>

          {(hoveredStatus.delayInfo?.type === 'delay' || hoveredStatus.delayInfo?.memberInfo) ? (
            <div className="d-flex flex-column gap-2">
              {(hoveredStatus.delayInfo?.message || hoveredStatus.delayInfo?.memberInfo?.message) && (
                <div style={{ color: '#e2e8f0', wordBreak: 'keep-all', fontSize: '0.74rem', lineHeight: '1.45' }}>
                  {hoveredStatus.delayInfo?.message || hoveredStatus.delayInfo?.memberInfo?.message}
                </div>
              )}
              {(() => {
                const pendings = hoveredStatus.delayInfo?.pendings || hoveredStatus.delayInfo?.memberInfo?.pendings || [];
                const progresses = hoveredStatus.delayInfo?.progresses || hoveredStatus.delayInfo?.memberInfo?.progresses || [];
                const dones = hoveredStatus.delayInfo?.dones || hoveredStatus.delayInfo?.memberInfo?.dones || [];
                const hasAny = pendings.length > 0 || progresses.length > 0 || dones.length > 0;
                if (!hasAny) return null;

                const hasMsg = Boolean(hoveredStatus.delayInfo?.message || hoveredStatus.delayInfo?.memberInfo?.message);

                return (
                  <div
                    style={{
                      maxHeight: '135px',
                      overflowY: 'auto',
                      overscrollBehavior: 'contain',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '8px',
                      paddingRight: '4px',
                      borderTop: hasMsg ? '1px dashed #4a5568' : 'none',
                      paddingTop: hasMsg ? '6px' : '0'
                    }}
                  >
                    {dones.length > 0 && (
                      <div>
                        <div className="fw-semibold mb-1" style={{ fontSize: '0.72rem', color: '#86efac' }}>
                          완료 ({dones.length}명)
                        </div>
                        <div className="d-flex flex-wrap gap-1">
                          {dones.map((name, i) => (
                            <span
                              key={i}
                              style={{
                                backgroundColor: '#14532d',
                                color: '#bbf7d0',
                                border: '1px solid #22c55e',
                                borderRadius: '3px',
                                padding: '2px 7px',
                                fontSize: '0.73rem',
                                fontWeight: 'normal'
                              }}
                            >
                              {name}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {progresses.length > 0 && (
                      <div>
                        <div className="fw-semibold mb-1" style={{ fontSize: '0.72rem', color: '#93c5fd' }}>
                          진행중 ({progresses.length}명)
                        </div>
                        <div className="d-flex flex-wrap gap-1">
                          {progresses.map((name, i) => (
                            <span
                              key={i}
                              style={{
                                backgroundColor: '#1e3a8a',
                                color: '#bfdbfe',
                                border: '1px solid #3b82f6',
                                borderRadius: '3px',
                                padding: '2px 7px',
                                fontSize: '0.73rem',
                                fontWeight: 'normal'
                              }}
                            >
                              {name}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {pendings.length > 0 && (
                      <div>
                        <div className="fw-semibold mb-1" style={{ fontSize: '0.72rem', color: '#fca5a5' }}>
                          미접수 ({pendings.length}명)
                        </div>
                        <div className="d-flex flex-wrap gap-1">
                          {pendings.map((name, i) => (
                            <span
                              key={i}
                              style={{
                                backgroundColor: '#4a5568',
                                color: '#fff',
                                borderRadius: '3px',
                                padding: '2px 7px',
                                fontSize: '0.73rem',
                                fontWeight: 'normal'
                              }}
                            >
                              {name}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>
          ) : (
            <div style={{ color: '#e2e8f0', wordBreak: 'keep-all', marginTop: '4px', fontSize: '0.74rem' }}>
              {hoveredStatus.delayInfo?.message || hoveredStatus.message}
            </div>
          )}

          <div style={{
            position: 'absolute',
            ...(isNearBottom ? { bottom: '10px' } : isNearTop ? { top: '10px' } : { top: '50%', marginTop: '-6px' }),
            left: hoveredStatus.showRight ? '-6px' : 'auto',
            right: hoveredStatus.showRight ? 'auto' : '-6px',
            borderWidth: hoveredStatus.showRight ? '6px 6px 6px 0' : '6px 0 6px 6px',
            borderStyle: 'solid',
            borderColor: hoveredStatus.showRight
              ? 'transparent #2d3748 transparent transparent'
              : 'transparent transparent transparent #2d3748'
          }} />
        </div>,
        document.body
      );
    })()}
    </div>
  );
}

export default WorkOrderMatrix;
